import { useMemo, useEffect } from "react";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { UNITS_PER_STEP } from "./constants";

// Scène de décor pendant le jeu (textures différentes de l'intro kiosk qui utilise Scene.glb)
const SCENE_URL = "/bg/Scene_game.glb";

// Échelle uniforme appliquée à chaque tuile.
const SCENE_SCALE = 4;

// Correction par défaut appliquée à l'espacement = BBox×scale. La BBox seule donne
// un raccord avec un micro-gap visible (overhangs sapins/touffes que la BBox ne capte
// pas). -0.5 unité = chevauchement minimal qui ferme le gap. Tuné via /decor-test sur
// Scene_game.glb v1 — si tu changes la GLB et que le raccord se voit à nouveau, retune
// via /decor-test et reporte ici la nouvelle valeur "Nudge actuel".
const TILE_GAP_NUDGE_DEFAULT = -0.5;

interface DecorProps {
  trackSlots: number;
  // Décalage manuel en unités monde appliqué à chaque tuile (cumulatif).
  // Négatif = chevauchement, positif = écart supplémentaire. Sert au tuning
  // visuel via le panneau debug (?debugDecor=1) — laisser 0 en prod.
  tileGapNudge?: number;
  // Callback optionnel pour reporter la longueur native d'une tuile au parent
  // (consommé par le panneau debug pour afficher la valeur de référence).
  onTileLengthMeasured?: (length: number) => void;
}

// Décor de fond : on charge la scène glb (qui contient tout le diorama : herbe, sapins,
// touffes, bordures, flèche, haies) et on la clone N fois pour la tiler le long de la piste.
// Les clones partagent la geometry et les materials, donc l'empreinte mémoire reste
// raisonnable même avec beaucoup de tuiles.
export function Decor({ trackSlots, tileGapNudge = 0, onTileLengthMeasured }: DecorProps) {
  const { scene } = useGLTF(SCENE_URL);

  // Longueur réelle d'une tuile = bounding box X de la GLB × échelle. Auparavant
  // hardcodée à 30.88 (mesurée à la main), ce qui laissait un micro-gap entre tuiles
  // dès que la BBox réelle divergeait. Calculée une fois au load.
  const tileLengthX = useMemo(() => {
    const bbox = new THREE.Box3().setFromObject(scene);
    return (bbox.max.x - bbox.min.x) * SCENE_SCALE;
  }, [scene]);

  useEffect(() => {
    onTileLengthMeasured?.(tileLengthX);
  }, [tileLengthX, onTileLengthMeasured]);

  // Espacement effectif = BBox×scale + nudge par défaut (corrige les overhangs) + nudge
  // explicite passé par le parent (utilisé par /decor-test pour tuning live, sinon 0).
  const tileStep = tileLengthX + TILE_GAP_NUDGE_DEFAULT + tileGapNudge;

  const trackLength = trackSlots * UNITS_PER_STEP;
  const startTile = -1;
  const endTile = Math.ceil(trackLength / tileStep);

  // Activer ombres sur tous les meshes de la scène d'origine (s'applique aux clones aussi
  // car ces flags se trouvent sur les Mesh, qui partagent leur référence après clone(true)).
  useEffect(() => {
    scene.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
  }, [scene]);

  // Un clone indépendant par tuile (deep clone — partage geometry/material par référence).
  const tiles = useMemo(() => {
    const arr: { index: number; node: THREE.Object3D }[] = [];
    for (let i = startTile; i <= endTile; i++) {
      arr.push({ index: i, node: scene.clone(true) });
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, endTile]);

  return (
    <group>
      {tiles.map(({ index, node }) => (
        <primitive
          key={`tile-${index}`}
          object={node}
          position={[index * tileStep, 0, 0]}
          scale={SCENE_SCALE}
        />
      ))}
    </group>
  );
}

useGLTF.preload(SCENE_URL);
